import {
  ALL_CLOSED_REQUESTS,
  ALL_OPEN_REQUESTS,
  ALL_REQUESTS,
  CREATE_CLIENT_ISSUE_REQUEST,
  CREATE_ISSUE_MANAGER,
  CREATE_IT_REQUEST,
  CREATE_REPORT_REQUEST,
  CREATE_REQUEST_EVENT,
  DELETE_ISSUE_MANAGER,
  DEPARTMENTS,
  IT_APPS,
  IT_REQUEST,
  IT_REQUEST_ISSUE_MANAGERS,
  MY_CLOSED_REQUESTS,
  MY_OPEN_REQUESTS,
  MY_REQUESTS,
  RequestEvent,
  RequestFragment,
  RequestIssueManagerFragment,
  SEARCH_REQUEST,
  TOGGLE_IMPORTANT,
  TOGGLE_SUBSCRIBED,
} from '@/rtk-query.gql';

import { enhancedApi } from '../api/baseApi';

interface RequestsResponse {
  openItRequests: { items: RequestEvent[] };
  closedItRequests: { items: RequestEvent[] };
  itRequests: { items: RequestEvent[] };
}

interface SearchRequestResponse {
  itRequestSearch: RequestFragment[];
}

interface RequestIssueManagersResponse {
  requestIssueManagers: { items: RequestIssueManagerFragment[] };
}

export const issuesApi = enhancedApi.injectEndpoints({
  endpoints: (build) => ({
    // Queries
    getMyOpenRequests: build.query({
      query: (variables) => ({
        document: MY_OPEN_REQUESTS,
        variables,
      }),
      transformResponse: (data: RequestsResponse) => data.openItRequests.items,
    }),
    getMyClosedRequests: build.query({
      query: (variables) => ({
        document: MY_CLOSED_REQUESTS,
        variables,
      }),
      transformResponse: (data: RequestsResponse) =>
        data.closedItRequests.items,
    }),
    getMyRequests: build.query({
      query: (variables) => ({
        document: MY_REQUESTS,
        variables,
      }),
      transformResponse: (data: RequestsResponse) => data.itRequests.items,
    }),
    getAllRequests: build.query({
      query: (variables) => ({
        document: ALL_REQUESTS,
        variables,
      }),
      transformResponse: (data: RequestsResponse) => data.itRequests.items,
    }),
    getAllOpenRequests: build.query({
      query: (variables) => ({
        document: ALL_OPEN_REQUESTS,
        variables,
      }),
      transformResponse: (data: RequestsResponse) => data.openItRequests.items,
    }),
    getAllClosedRequests: build.query({
      query: (variables) => ({
        document: ALL_CLOSED_REQUESTS,
        variables,
      }),
      transformResponse: (data: RequestsResponse) =>
        data.closedItRequests.items,
    }),
    searchRequest: build.query({
      query: (variables) => ({
        document: SEARCH_REQUEST,
        variables,
      }),
      transformResponse: (data: SearchRequestResponse) => data.itRequestSearch,
    }),
    getItRequest: build.query({
      query: (variables) => ({
        document: IT_REQUEST,
        variables,
      }),
      providesTags: ['IT_REQUEST'],
    }),
    getItApps: build.query({
      query: () => ({
        document: IT_APPS,
      }),
    }),
    getDepartments: build.query({
      query: () => ({
        document: DEPARTMENTS,
      }),
    }),
    getIssueManagers: build.query({
      query: () => ({
        document: IT_REQUEST_ISSUE_MANAGERS,
      }),
      providesTags: ['IT_REQUEST_ISSUE_MANAGERS'],
      transformResponse: (data: RequestIssueManagersResponse) =>
        data.requestIssueManagers.items,
    }),

    // Mutations
    createItRequest: build.mutation({
      query: (variables) => ({
        document: CREATE_IT_REQUEST,
        variables,
        invalidatesTags: ['IT_REQUEST'],
      }),
    }),
    createClientIssueRequest: build.mutation({
      query: (variables) => ({
        document: CREATE_CLIENT_ISSUE_REQUEST,
        variables,
        invalidatesTags: ['IT_REQUEST'],
      }),
    }),
    createReportRequest: build.mutation({
      query: (variables) => ({
        document: CREATE_REPORT_REQUEST,
        variables,
        invalidatesTags: ['IT_REQUEST'],
      }),
    }),
    createRequestEvent: build.mutation({
      query: (variables) => ({
        document: CREATE_REQUEST_EVENT,
        variables,
      }),
      invalidatesTags: ['IT_REQUEST'],
    }),
    toggleSubscribed: build.mutation({
      query: (variables) => ({
        document: TOGGLE_SUBSCRIBED,
        variables,
      }),
      invalidatesTags: ['IT_REQUEST'],
    }),
    toggleImportant: build.mutation({
      query: (variables) => ({
        document: TOGGLE_IMPORTANT,
        variables,
        invalidatesTags: ['IT_REQUEST'],
      }),
    }),
    createIssueManager: build.mutation({
      query: (variables) => ({
        document: CREATE_ISSUE_MANAGER,
        variables,
      }),
      invalidatesTags: ['IT_REQUEST_ISSUE_MANAGERS'],
    }),
    deleteIssueManager: build.mutation({
      query: (variables) => ({
        document: DELETE_ISSUE_MANAGER,
        variables,
      }),
      invalidatesTags: ['IT_REQUEST_ISSUE_MANAGERS'],
    }),
  }),
});

export const {
  useGetMyOpenRequestsQuery,
  useGetMyClosedRequestsQuery,
  useGetMyRequestsQuery,
  useGetAllRequestsQuery,
  useGetAllOpenRequestsQuery,
  useGetAllClosedRequestsQuery,
  useSearchRequestQuery,
  useGetItRequestQuery,
  useGetItAppsQuery,
  useGetDepartmentsQuery,
  useGetIssueManagersQuery,
  useCreateItRequestMutation,
  useCreateClientIssueRequestMutation,
  useCreateReportRequestMutation,
  useCreateRequestEventMutation,
  useToggleSubscribedMutation,
  useToggleImportantMutation,
  useCreateIssueManagerMutation,
  useDeleteIssueManagerMutation,
} = issuesApi;
